import React, { Component } from "react";
import { Select, t } from "../../../components/CustomComponent.js";
import HomeMedia from "./HomeMedia.js";
import ICON from "../icons.js";
import { DEAL_OPTIONS, PRICE_OPTIONS, BED_OPTIONS } from "../options.js";

/* ============================================================================
 * HomeHero — ภาพหลัก + พาดหัว + กล่องค้นหาทรัพย์
 * - ภาพพื้นหลังสไลด์เปลี่ยนไปทีละภาพเรื่อย ๆ (ค่อย ๆ จางสลับ) จัดการรายการภาพ
 *   และจังหวะเปลี่ยนได้จากหลังบ้าน /ad/home/hero
 * - กล่องค้นหาเป็นแถบเดียว 6 ช่อง + ปุ่มค้นหา
 *   ทำเล / ประเภททรัพย์ / โครงการ มาจาก API (เฉพาะที่มีทรัพย์อยู่จริง) ส่วนช่วงราคา/ห้องนอน
 *   เป็นการจัดกลุ่มของ UI จึงเป็นค่าคงที่ใน options.js
 *   ค่าที่เลือกเก็บใน state ทั้งหมด แล้วส่งกลับผ่าน props.onSearch
 * ========================================================================== */
export default class HomeHero extends Component {
  state = {
    deal: "",
    zone: "",
    type: "",
    project: "",
    price: "",
    bed: "",
    slide: 0,
  };

  componentDidMount() {
    this.startAutoplay();
  }

  componentDidUpdate(prevProps) {
    // ภาพมาทีหลัง (โหลดจาก API เสร็จ) จึงต้องตั้งนาฬิกาใหม่เมื่อจำนวนภาพเปลี่ยน
    if (prevProps.hero !== this.props.hero) this.startAutoplay();
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  slides = () => this.props.hero?.slides || [];

  startAutoplay = () => {
    clearInterval(this.timer);
    const setting = this.props.hero?.setting || {};
    if (setting.autoplay === false || this.slides().length < 2) return;
    this.timer = setInterval(
      () => this.setState((s) => ({ slide: s.slide + 1 })),
      Math.max(2, Number(setting.interval) || 6) * 1000,
    );
  };

  // เปลี่ยนประเภทประกาศแล้วล้างช่วงราคาที่ผูกกับค่าเดิม (เช่า/ขาย คนละชุดกัน)
  selectDeal = (deal) => this.setState({ deal, price: "" });

  submit = () => {
    const { onSearch } = this.props;
    if (onSearch) onSearch({ ...this.state });
  };

  // ช่วงราคา : ประกาศเช่าใช้หน่วยบาท/เดือน ที่เหลือใช้หน่วยล้านบาท
  priceOptions = () => (this.state.deal === "rent" ? PRICE_OPTIONS.rent : PRICE_OPTIONS.sale);

  /* ตัวเลือกในรายการรับได้ 2 แบบ : string (เป็น key แปลหรือข้อความตรง ๆ)
     และ object {value,label} — แปลงให้เป็นรูปแบบเดียวก่อนส่งให้ Select
     label ที่มาจาก API เป็นข้อความจริงอยู่แล้ว t() จะคืนค่าเดิมกลับมา */
  toOptions = (options) =>
    (options || []).map((opt) =>
      typeof opt === "string" ? { value: opt, label: t(opt) } : { value: opt.value, label: t(opt.label) },
    );

  /* ใช้ Select ของระบบ (พิมพ์ค้นหาในรายการได้) ชุดเดียวกับหน้าอื่นในโปรเจกต์
     ช่องเดียวจบในบรรทัดเดียว : ไอคอน + ค่าที่เลือก (ยังไม่เลือก = ชื่อช่อง)
     nowrap = รายการที่เด้งขึ้นมากว้างตามชื่อที่ยาวที่สุด แต่ละตัวเลือกจึงอ่านได้เต็ม
     บรรทัดเดียว (ชื่อโครงการยาวกว่าช่อง) โดยช่องค้นหาเองยังกว้างเท่าเดิม */
  field = (icon, value, options, onChange, placeholder) => (
    <div className="hm-field">
      <span className="hm-field__icon hm-ico">{icon}</span>
      <Select
        className="hm-field__select"
        default={false}
        nowrap
        placeholder={t(placeholder)}
        value={value}
        options={[{ value: "", label: t(placeholder) }, ...this.toOptions(options)]}
        onChange={(selected) => onChange(selected)}
      />
    </div>
  );

  render() {
    const { zones = [], types = [], project_names = [] } = this.props;
    const { deal, zone, type, project, price, bed, slide } = this.state;
    const slides = this.slides();
    const current = slides.length ? slide % slides.length : 0;

    return (
      <section className="hm-hero">
        {/* ภาพพื้นหลัง : ซ้อนทุกภาพไว้แล้วสลับความทึบ ทำให้เปลี่ยนภาพแบบค่อย ๆ จาง */}
        <div className="hm-hero__bg">
          {slides.map((item, i) => (
            <div key={item.uid} className={`hm-hero__slide ${i === current ? "is-active" : ""}`} aria-hidden={i !== current}>
              {item.src ? (
                <img src={item.src} alt={item.name || t("home_hero_image_alt")} style={{ objectPosition: item.position || "center" }} />
              ) : (
                <HomeMedia kind={item.kind} tone={item.tone} ratio={0.42} alt={item.name} className="hm-hero__scene" />
              )}
            </div>
          ))}
        </div>
        <div className="hm-hero__veil" />

        <div className="hm-container">
          <div className="hm-hero__head">
            <h1 className="hm-hero__title">
              {t("home_hero_title_1")}
              <span>{t("home_hero_title_2")}</span>
            </h1>
            <p className="hm-hero__sub">
              {t("home_hero_sub_1")}
              <br />
              {t("home_hero_sub_2")}
            </p>
          </div>

          {/* ---------------------------------------------------- กล่องค้นหา */}
          <div className="hm-search">
            <div className="hm-search__panel">
              {this.field(ICON.marker, zone, zones, (v) => this.setState({ zone: v }), "home_opt_zone_any")}
              {this.field(ICON.building, type, types, (v) => this.setState({ type: v }), "home_field_type")}
              {/* โครงการ — ตัวเลือกคือชื่อโครงการที่มีประกาศอยู่จริง พิมพ์ค้นในรายการได้ */}
              {this.field(ICON.town, project, project_names, (v) => this.setState({ project: v }), "home_opt_project_any")}
              {this.field(ICON.tag, deal, DEAL_OPTIONS, this.selectDeal, "home_field_deal_any")}
              {this.field(ICON.money, price, this.priceOptions(), (v) => this.setState({ price: v }), "home_field_price_any")}
              {this.field(ICON.bed, bed, BED_OPTIONS, (v) => this.setState({ bed: v }), "home_field_bed")}

              <button type="button" className="hm-btn hm-btn--primary hm-search__submit" onClick={this.submit}>
                <span className="hm-ico">{ICON.search}</span>
                {t("home_search_submit")}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }
}
